import { z } from 'zod';

/**
 * In-app notification DTOs (SIL-157, layer 2 of the notifications epic SIL-155).
 *
 * The DB layer (SIL-156, db/schema/647_notification.sql) stores one `notification`
 * row per event surfaced to a customer_org's feed: an org-scoped `type` (one of 6
 * v1 events), a type-specific `payload` jsonb, a `link_path`, and a `created_at`.
 * Read state is tracked per client user in `notification_read`.
 *
 * This module is the SHARED contract the API read endpoints and the client UI
 * both build on. Two rules the design enforces:
 *
 *   1. The 6-value `type` union is the ONE source of truth on the TS side. It is
 *      drift-guarded against the DB CHECK enum (and thus emit_notification()) by
 *      __tests__/dto/notification.test.ts, which parses the values straight out
 *      of the migration. Change the set in exactly one place and the guard tells
 *      every consumer to catch up.
 *   2. Payloads carry STRUCTURED FACTS, never pre-rendered copy. The frontend owns
 *      the wording; the backend owns the facts. Each `type` pairs with exactly one
 *      payload shape via a discriminated union so a renderer can switch on `type`
 *      and get a fully-typed payload with no casts.
 */

// ---------------------------------------------------------------------------
// Notification type — the single source of truth for the 6 v1 event types.
// Mirrors the `type IN (...)` CHECK in db/schema/647_notification.sql exactly;
// the drift guard test fails if the two lists ever diverge.
// ---------------------------------------------------------------------------
export const NOTIFICATION_TYPES = [
  'submission_approved',
  'submission_rejected',
  'offer_received',
  'offer_rescinded',
  'offer_refused',
  'purchase_completed',
  // 671: forward-sale events (669/670 forward availability).
  'forward_sale_committed',
  'settlement_reminder',
  'forward_sale_settled',
  'listing_expired',
  // 766: auction close (SIL-650) — bid -> offer conversion outcomes.
  'auction_won',
  'auction_lost',
  // 767 (SIL-651): overdue transaction_requirement checklist step nag.
  'requirement_overdue',
  // 953 (SIL-1327/SIL-1313): Exchange buyer verification (KYB) lifecycle.
  // 'buyer_verification_submitted' is a receipt to the submitting org (sent
  // by the client submit route). The other 3 are emitted by the ops review
  // routes (SIL-1330).
  'buyer_verification_submitted',
  'buyer_verification_approved',
  'buyer_verification_rejected',
  'buyer_verification_revoked',
  // 972 (SIL-1425): exchange sweep expiry of an unanswered buy_now offer —
  // emitted to both the buyer and the seller.
  'buy_now_expired',
  // 973 (SIL-1428): a business buyer expressed non-binding interest in a
  // listing — sent to the seller only. The seller never learns who sent it
  // (v_listing_interest_for_seller / listing_interest_seller_rows() is the
  // only read path to the underlying row, and it omits both customer_org_id
  // and client_user_id).
  'listing_interest_received',
  // 977 (SIL-1446): the three buy-now (kind='buy_now' offer, 972) lifecycle
  // notifications. buy_now_requested goes to the SELLER when a buyer submits
  // a buy-now request; buy_now_confirmed / buy_now_declined go to the BUYER
  // when the seller accepts / rejects that request (never for a normal
  // offer — those keep using offer_received / offer accept-reject has no
  // dedicated buyer-facing type today).
  'buy_now_requested',
  'buy_now_confirmed',
  'buy_now_declined',
  // 1010 (SIL-1570): an open offer was expired because the arbitrage source
  // went sold/missing and the house listing was withdrawn — sent to the
  // BUYER org (one per distinct buyer org).
  'offer_expired',
] as const;

export const notificationTypeSchema = z.enum(NOTIFICATION_TYPES);
export type NotificationType = z.infer<typeof notificationTypeSchema>;

// ---------------------------------------------------------------------------
// Per-event payload schemas — structured facts the frontend renders into copy.
// NO pre-rendered strings. Field names are camelCase to match the rest of the
// DTO layer (the jsonb column stores exactly these keys).
// ---------------------------------------------------------------------------

/** A client's asset submission was approved by ops. */
export const submissionApprovedPayloadSchema = z.object({
  submissionId: z.string().uuid(),
  assetCount: z.number().int().nonnegative(),
  assetClass: z.string(),
});
export type SubmissionApprovedPayload = z.infer<typeof submissionApprovedPayloadSchema>;

/** A client's asset submission was rejected by ops. `reason` is optional — ops may reject without one (SIL-161). */
export const submissionRejectedPayloadSchema = z.object({
  submissionId: z.string().uuid(),
  assetCount: z.number().int().nonnegative(),
  assetClass: z.string(),
  reason: z.string().optional(),
});
export type SubmissionRejectedPayload = z.infer<typeof submissionRejectedPayloadSchema>;

/** A buyer submitted an offer against the recipient (seller) org's listing. */
export const offerReceivedPayloadSchema = z.object({
  offerId: z.string().uuid(),
  listingId: z.string().uuid(),
  amount: z.number(),
  currency: z.string(),
  skuLabel: z.string(),
});
export type OfferReceivedPayload = z.infer<typeof offerReceivedPayloadSchema>;

/** A buyer rescinded (withdrew) a pending offer. */
export const offerRescindedPayloadSchema = z.object({
  offerId: z.string().uuid(),
  listingId: z.string().uuid(),
  skuLabel: z.string(),
});
export type OfferRescindedPayload = z.infer<typeof offerRescindedPayloadSchema>;

/** Ops refused an offer, with a required reason (mirrors RUF-572 refusal). */
export const offerRefusedPayloadSchema = z.object({
  offerId: z.string().uuid(),
  listingId: z.string().uuid(),
  reason: z.string(),
  skuLabel: z.string(),
});
export type OfferRefusedPayload = z.infer<typeof offerRefusedPayloadSchema>;

/** An offer completed into a purchase / ownership transfer. */
export const purchaseCompletedPayloadSchema = z.object({
  offerId: z.string().uuid(),
  transactionId: z.string().uuid(),
  amount: z.number(),
  currency: z.string(),
  skuLabel: z.string(),
});
export type PurchaseCompletedPayload = z.infer<typeof purchaseCompletedPayloadSchema>;

/** 671: deposit captured on a future-dated listing — the deal is binding; settles on availableOn. Sent to both orgs. */
export const forwardSaleCommittedPayloadSchema = z.object({
  offerId: z.string().uuid(),
  listingId: z.string().uuid(),
  amount: z.number(),
  currency: z.string(),
  skuLabel: z.string(),
  availableOn: z.string(),
  /** 'seller' | 'buyer' — which side of the deal this feed row's org is on. */
  side: z.enum(['seller', 'buyer']),
});
export type ForwardSaleCommittedPayload = z.infer<typeof forwardSaleCommittedPayloadSchema>;

/** 671: T-7-days settlement heads-up (sweep batch). */
export const settlementReminderPayloadSchema = z.object({
  listingId: z.string().uuid(),
  skuLabel: z.string(),
  availableOn: z.string(),
  side: z.enum(['seller', 'buyer']),
});
export type SettlementReminderPayload = z.infer<typeof settlementReminderPayloadSchema>;

/** 671: the forward sale settled — ownership transferred (sweep). */
export const forwardSaleSettledPayloadSchema = z.object({
  offerId: z.string().uuid(),
  listingId: z.string().uuid(),
  amount: z.number(),
  currency: z.string(),
  skuLabel: z.string(),
  side: z.enum(['seller', 'buyer']),
});
export type ForwardSaleSettledPayload = z.infer<typeof forwardSaleSettledPayloadSchema>;

/** 671: an active listing (or open direct offer) auto-expired (sweep). */
export const listingExpiredPayloadSchema = z.object({
  /** Exactly one of listingId / directOfferId is set. */
  listingId: z.string().uuid().nullable(),
  directOfferId: z.string().uuid().nullable(),
  skuLabel: z.string(),
});
export type ListingExpiredPayload = z.infer<typeof listingExpiredPayloadSchema>;

/** 766 (SIL-650): the caller's bid won the closed auction — converted into an offer. */
export const auctionWonPayloadSchema = z.object({
  listingId: z.string().uuid(),
  offerId: z.string().uuid(),
  amount: z.number(),
  currency: z.string(),
  skuLabel: z.string(),
  /** false = reserve was not met; the offer landed as `pending` rather than `accepted`. */
  reserveMet: z.boolean(),
});
export type AuctionWonPayload = z.infer<typeof auctionWonPayloadSchema>;

/**
 * 766 (SIL-650): the caller bid on a closed auction but did not win.
 *
 * SIL-1334: `winningBidAmount` is the exact final winning bid, present ONLY
 * when the recipient org was a verified buyer at emit time. An unverified
 * recipient instead gets `winningBidRangeLowUsd`/`winningBidRangeHighUsd` —
 * the same bucket range listing_price_range() (954/db, SIL-1328) would
 * produce for that price on the marketplace. Exactly one of the pair is
 * ever present; never both, never neither.
 */
export const auctionLostPayloadSchema = z.object({
  listingId: z.string().uuid(),
  skuLabel: z.string(),
  winningBidAmount: z.number().optional(),
  winningBidRangeLowUsd: z.number().optional(),
  winningBidRangeHighUsd: z.number().optional(),
  currency: z.string(),
});
export type AuctionLostPayload = z.infer<typeof auctionLostPayloadSchema>;

/** 767 (SIL-651): an unsatisfied transaction_requirement step passed its due_at (sweep). */
export const requirementOverduePayloadSchema = z.object({
  offerId: z.string().uuid(),
  stepKey: z.string(),
  dueAt: z.string(),
  skuLabel: z.string(),
  /** 'seller' | 'buyer' — no per-step owner column exists (760 kept the template dumb), so both
   *  transaction parties are notified, mirroring settlement_reminder's same choice (671/672). */
  side: z.enum(['seller', 'buyer']),
});
export type RequirementOverduePayload = z.infer<typeof requirementOverduePayloadSchema>;

/** 973 (SIL-1428): a business buyer expressed interest in the recipient (seller) org's listing. No buyer identity — see this file's module header. */
export const listingInterestReceivedPayloadSchema = z.object({
  listingId: z.string().uuid(),
  interestId: z.string().uuid(),
  skuLabel: z.string(),
});
export type ListingInterestReceivedPayload = z.infer<typeof listingInterestReceivedPayloadSchema>;

/** 977 (SIL-1446): a buyer submitted a buy-now request on the recipient (seller) org's listing. */
export const buyNowRequestedPayloadSchema = z.object({
  offerId: z.string().uuid(),
  listingId: z.string().uuid(),
  amount: z.number(),
  currency: z.string(),
  skuLabel: z.string(),
});
export type BuyNowRequestedPayload = z.infer<typeof buyNowRequestedPayloadSchema>;

/** 977 (SIL-1446): the seller accepted the recipient (buyer) org's buy-now request. */
export const buyNowConfirmedPayloadSchema = z.object({
  offerId: z.string().uuid(),
  listingId: z.string().uuid(),
  amount: z.number(),
  currency: z.string(),
  skuLabel: z.string(),
});
export type BuyNowConfirmedPayload = z.infer<typeof buyNowConfirmedPayloadSchema>;

/** 977 (SIL-1446): the seller rejected the recipient (buyer) org's buy-now request. */
export const buyNowDeclinedPayloadSchema = z.object({
  offerId: z.string().uuid(),
  listingId: z.string().uuid(),
  skuLabel: z.string(),
});
export type BuyNowDeclinedPayload = z.infer<typeof buyNowDeclinedPayloadSchema>;

/** 1010 (SIL-1570): the recipient (buyer) org's open offer expired — the item is no longer available. */
export const offerExpiredPayloadSchema = z.object({
  offerId: z.string().uuid(),
  listingId: z.string().uuid(),
  skuLabel: z.string(),
  reason: z.enum(['source_sold', 'source_missing']),
});
export type OfferExpiredPayload = z.infer<typeof offerExpiredPayloadSchema>;

/**
 * Discriminated union over `type` — one payload shape per event. A renderer can
 * `switch (n.type)` and get a fully-narrowed payload with no casts.
 */
export const notificationPayloadSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('submission_approved'), payload: submissionApprovedPayloadSchema }),
  z.object({ type: z.literal('submission_rejected'), payload: submissionRejectedPayloadSchema }),
  z.object({ type: z.literal('offer_received'), payload: offerReceivedPayloadSchema }),
  z.object({ type: z.literal('offer_rescinded'), payload: offerRescindedPayloadSchema }),
  z.object({ type: z.literal('offer_refused'), payload: offerRefusedPayloadSchema }),
  z.object({ type: z.literal('purchase_completed'), payload: purchaseCompletedPayloadSchema }),
  z.object({ type: z.literal('forward_sale_committed'), payload: forwardSaleCommittedPayloadSchema }),
  z.object({ type: z.literal('settlement_reminder'), payload: settlementReminderPayloadSchema }),
  z.object({ type: z.literal('forward_sale_settled'), payload: forwardSaleSettledPayloadSchema }),
  z.object({ type: z.literal('listing_expired'), payload: listingExpiredPayloadSchema }),
  z.object({ type: z.literal('auction_won'), payload: auctionWonPayloadSchema }),
  z.object({ type: z.literal('auction_lost'), payload: auctionLostPayloadSchema }),
  z.object({ type: z.literal('requirement_overdue'), payload: requirementOverduePayloadSchema }),
  z.object({ type: z.literal('listing_interest_received'), payload: listingInterestReceivedPayloadSchema }),
  z.object({ type: z.literal('buy_now_requested'), payload: buyNowRequestedPayloadSchema }),
  z.object({ type: z.literal('buy_now_confirmed'), payload: buyNowConfirmedPayloadSchema }),
  z.object({ type: z.literal('buy_now_declined'), payload: buyNowDeclinedPayloadSchema }),
  z.object({ type: z.literal('offer_expired'), payload: offerExpiredPayloadSchema }),
]);
export type NotificationPayload = z.infer<typeof notificationPayloadSchema>;

// ---------------------------------------------------------------------------
// Read API response shapes.
// ---------------------------------------------------------------------------

// Envelope fields shared by every feed row, independent of `type`. `read` is the
// per-user derived flag (a row in notification_read for the requesting client
// user), NOT a column on `notification`.
const notificationEnvelope = {
  id: z.string().uuid(),
  linkPath: z.string(),
  createdAt: z.string(),
  read: z.boolean(),
};

/**
 * A single notification as returned by the feed read endpoint. Discriminated on
 * `type` so `type` and `payload` stay correlated at both the type and the
 * runtime-validation level: NotificationDTO = { id, type, payload, linkPath,
 * createdAt, read }, with `payload` narrowed by `type`.
 */
export const notificationSchema = z.discriminatedUnion('type', [
  z.object({ ...notificationEnvelope, type: z.literal('submission_approved'), payload: submissionApprovedPayloadSchema }),
  z.object({ ...notificationEnvelope, type: z.literal('submission_rejected'), payload: submissionRejectedPayloadSchema }),
  z.object({ ...notificationEnvelope, type: z.literal('offer_received'), payload: offerReceivedPayloadSchema }),
  z.object({ ...notificationEnvelope, type: z.literal('offer_rescinded'), payload: offerRescindedPayloadSchema }),
  z.object({ ...notificationEnvelope, type: z.literal('offer_refused'), payload: offerRefusedPayloadSchema }),
  z.object({ ...notificationEnvelope, type: z.literal('purchase_completed'), payload: purchaseCompletedPayloadSchema }),
  z.object({ ...notificationEnvelope, type: z.literal('forward_sale_committed'), payload: forwardSaleCommittedPayloadSchema }),
  z.object({ ...notificationEnvelope, type: z.literal('settlement_reminder'), payload: settlementReminderPayloadSchema }),
  z.object({ ...notificationEnvelope, type: z.literal('forward_sale_settled'), payload: forwardSaleSettledPayloadSchema }),
  z.object({ ...notificationEnvelope, type: z.literal('listing_expired'), payload: listingExpiredPayloadSchema }),
  z.object({ ...notificationEnvelope, type: z.literal('auction_won'), payload: auctionWonPayloadSchema }),
  z.object({ ...notificationEnvelope, type: z.literal('auction_lost'), payload: auctionLostPayloadSchema }),
  z.object({ ...notificationEnvelope, type: z.literal('requirement_overdue'), payload: requirementOverduePayloadSchema }),
  z.object({ ...notificationEnvelope, type: z.literal('listing_interest_received'), payload: listingInterestReceivedPayloadSchema }),
  z.object({ ...notificationEnvelope, type: z.literal('buy_now_requested'), payload: buyNowRequestedPayloadSchema }),
  z.object({ ...notificationEnvelope, type: z.literal('buy_now_confirmed'), payload: buyNowConfirmedPayloadSchema }),
  z.object({ ...notificationEnvelope, type: z.literal('buy_now_declined'), payload: buyNowDeclinedPayloadSchema }),
  z.object({ ...notificationEnvelope, type: z.literal('offer_expired'), payload: offerExpiredPayloadSchema }),
]);
export type NotificationDTO = z.infer<typeof notificationSchema>;

/** The feed list response. */
export const notificationListResponseSchema = z.object({
  data: z.array(notificationSchema),
});
export type NotificationListResponse = z.infer<typeof notificationListResponseSchema>;

/** The unread-count response (bell badge). */
export const notificationUnreadCountResponseSchema = z.object({
  count: z.number().int().nonnegative(),
});
export type NotificationUnreadCountResponse = z.infer<typeof notificationUnreadCountResponseSchema>;
